import { Component, Element, Event, EventEmitter, h, Host, Method, Prop, State, Listen } from '@stencil/core';
import { HTMLStencilElement, Watch } from '@stencil/core/internal';
import cls from 'classnames';
import debounce from 'lodash.debounce';
import merge from 'lodash.merge';
import { graphql } from '../../../../../hcl-sdk-core';
import { searchLocationWithParams as hcoSearchLocationWithParams } from '../../../core/api/hco';
import { searchLocationWithParams } from '../../../core/api/hcp';
import { getAddressFromGeo } from '../../../core/api/searchGeo';
import { COUNTRIES_LABELS, COUNTRY_CODE_BASE, CountryCode, GET_FULL_LIST_COUNTRY_CODE } from '../../../core/constants/countries';
import { BREAKPOINT_MAX_WIDTH, GEOLOC, NEAR_ME_ITEM, SCREEN_NAME, SEARCH_TARGET } from '../../../core/constants';
import { configStore, i18nStore, routerStore, searchMapStore, uiStore } from '../../../core/stores';
import { HclSDKConfigData, MapProvider, ModeViewType, NestedRecordString } from '../../../core/stores/ConfigStore';
import { GeolocCoordinates, InitScreen, WidgetProps, WidgetType } from '../../../core/types';
import { dateUtils } from '../../../utils/dateUtils';
import { applyDefaultTheme, getCurrentPosition as defaultGetCurrentPosition, isLocationObject } from '../../../utils/helper';
import { getI18nLabels, t } from '../../../utils/i18n';
import { OKSDK_GEOLOCATION_HISTORY, storageUtils } from '../../../utils/storageUtils';
import { ROUTER_PATH } from '../../hcl-sdk-router/constants';
import { observeTogglingSlot } from '../../../utils/slotObserver';

const isShowcase = false;

const defaults = {
  apiKey: '',
  isShowcase,
  disableCollectGeo: false,
  i18nBundlesPath: process.env.DEFAULT_I18N_BUNDLE_PATH,
  flagBundlesPath: process.env.DEFAULT_I18N_BUNDLE_PATH.replace('i18n', 'assets'),
};

export interface HclSDKInitConfigData extends HclSDKConfigData {
  getCurrentPosition?: (success: (coords: GeolocCoordinates) => void, error: (err: any) => void) => void;
}
@Component({
  tag: 'hcl-sdk',
  styleUrl: 'hcl-sdk.scss',
  shadow: true,
})
export class HclSDK {
  @Element() el: HTMLStencilElement;
  @Event() onClickSDK: EventEmitter<any>;
  @State() retriesCounter: number = 0;
  @State() loading = false;
  @State() slotState: Record<string, { show: boolean }> = {};
  @Prop() widget?: WidgetType;
  @Prop() widgetProps?: WidgetProps;
  @Prop() initScreen?: InitScreen;
  @Prop() currentPosition: {
    lat: number;
    lng: number;
  };
  private i18nCustomObject: NestedRecordString | undefined = undefined
  private observerSlot: MutationObserver;

  @Watch('currentPosition')
  userPositionChangeHandler(newProps) {
    if (this.widget) return;
    const { lat, lng } = newProps;

    searchMapStore.setState({ isShowRelaunchBtn: false });

    getAddressFromGeo(lat, lng).then(res => {
      if (res?.address?.country_code) {
        configStore.setState({
          countryGeo: res.address.country_code,
        });

        const { specialtyCode, specialtyLabel, screenName } = configStore.entryConfig;
        if (configStore && screenName && screenName === SCREEN_NAME.SEARCH_NEAR_ME) {
          if (!specialtyCode) {
            console.error('missing specialtyCode for "near me" search');
            return;
          }
          this.searchNearMe({ specialtyCode, specialtyLabel });
        }

        if (configStore && screenName && configStore.state.entry.screenName === SCREEN_NAME.SEARCH_BY_LOCATION) {
          if (configStore.state.entry.defaultLocation === 'nearMe') {
            this.searchNearMe({ specialtyCode, specialtyLabel, searchMode: configStore.initialSearchMode });
          }
        }
      }
    });

    searchMapStore.setGeoLocation({ latitude: lat, longitude: lng });
  }

  parentEl;

  componentWillLoad() {
    if (this.initScreen === 'search') {
      this.loadInitScreenSearch();
    }
    this.slotState.rightHCP = !!this.el.querySelector('[slot="hcp-right-panel"]') ? { show: false } : undefined;
  }

  componentDidLoad() {
    this.observerSlot = observeTogglingSlot({
      rootEl: this.el,
      slotName: 'hcp-right-panel',
      onAdd: () => {
        this.slotState = { ...this.slotState, rightHCP: { show: true } };
      },
      onRemove: () => {
        this.slotState = { ...this.slotState, rightHCP: { show: false } };
      },
    });
  }

  disconnectedCallback() {
    if (this.observerSlot) {
      this.observerSlot.disconnect();
    }
  }

  @Listen('onHCPDetail')
  showSlotDetail(e) {
    if (!this.el.querySelector('[slot="hcp-right-panel"]')) {
      return;
    }
    if (e.detail) {
      this.slotState = { ...this.slotState, rightHCP: { show: true } };
    } else {
      this.slotState = { ...this.slotState, rightHCP: { show: false } };
    }
  }

  @Method()
  updateConfig(patch: any): Promise<HclSDKConfigData> {
    const mapConfig = this.getMapConfig({ ...configStore.state, ...patch });
    configStore.setState(merge({}, configStore.state, patch, { map: mapConfig }));
    return Promise.resolve(configStore.state);
  }

  @Method()
  backToHome() {
    routerStore.push('/');
  }

  @Method()
  async searchNearMe({
    specialtyCode = [],
    specialtyLabel = '',
    searchMode = SEARCH_TARGET.HCP,
  }: { specialtyCode?: string[]; specialtyLabel?: string; searchMode?: SEARCH_TARGET } = {}) {
    if (this.widget) return;

    searchMapStore.setSearchFieldValue('address', t('near_me'));
    if (specialtyLabel) {
      searchMapStore.setSearchFieldValue('specialtyName', specialtyLabel);
    }
    searchMapStore.setState({
      locationFilter: NEAR_ME_ITEM,
      specialties: [],
      specialtiesRaw: [],
      specialtyFilter: specialtyCode.map(code => ({ id: code, name: specialtyLabel })),
      searchTarget: searchMode,
    });
    configStore.setState({
      modeView: ModeViewType.MAP,
      modals: [],
    });
    if (routerStore.state.currentRoutePath !== ROUTER_PATH.SEARCH_RESULT) {
      routerStore.push('/search-result');
      return;
    }
    if (searchMode === SEARCH_TARGET.HCP) {
      searchLocationWithParams();
    } else {
      hcoSearchLocationWithParams();
    }
  }

  @Method()
  async init({ getCurrentPosition, ...config }: Partial<HclSDKInitConfigData>) {
    if (config.apiKey === undefined) {
      throw new Error('Please provide an apiKey to the configuration object.');
    }

    const mapConfig = this.getMapConfig(config);
    const initConfig = merge({}, defaults, config, { map: mapConfig });
    const lang = (() => {
      if (config.locale) {
        return config.locale;
      }

      const closestElement = this.el.closest('[lang]') as HTMLElement;
      const _lang = closestElement ? closestElement.lang : i18nStore.state.lang;
      if (closestElement) {
        this.observeChangeLang(closestElement);
      }
      return _lang;
    })();
    this.i18nCustomObject = initConfig.i18nCustomObject
    await Promise.all([
      getI18nLabels(lang, initConfig.i18nCustomObject),
      this.loadCountriesFromMyKey({
        apiKey: initConfig.apiKey,
      }),
    ]);

    if (config?.entry?.screenName === SCREEN_NAME.SEARCH_NEAR_ME || config?.entry?.screenName === SCREEN_NAME.SEARCH_BY_LOCATION) {
      this.loadInitScreenSearch(); // Change the route to search immediately to avoid a flash screen
    }

    const premadeGetCurrentPosition = success => {
      success({
        latitude: this.currentPosition.lat,
        longitude: this.currentPosition.lng,
      });
    };
    // For this specific case, we don't want to collect the geolocation to messed it up with current user Geolocation country
    // ToDO: Refactor this part to make it more dynamic with HCO, HCP, etc.
    const isSearchByLocationWithInputCoords = config.entry?.screenName === SCREEN_NAME.SEARCH_BY_LOCATION && isLocationObject(config.entry.defaultLocation);

    this.loadCurrentPosition({
      isShowcase,
      getCurrentPosition: this.currentPosition ? premadeGetCurrentPosition : getCurrentPosition,
      disableCollectGeo: config.disableCollectGeo || isSearchByLocationWithInputCoords,
    });

    if (isSearchByLocationWithInputCoords) {
      const { lat, lng } = config.entry.defaultLocation as { lat: number; lng: number };
      initConfig.modeView = ModeViewType.MAP;
      initConfig.modal = undefined;
      getAddressFromGeo(Number(lat), Number(lng)).then(res => {
        if (res?.address?.country_code) {
          configStore.setState({
            countryFilterSelected: res.address.country_code,
          });
        }
        if (res?.shortDisplayName) {
          searchMapStore.setSearchFieldValue('address', res.shortDisplayName);
        }
        // Don't assign addressDetails in the first time to avoid distanceDefault being override by boundingbox
        searchMapStore.setState({
          locationFilter: { lat: String(lat), lng: String(lng) },
          specialties: [],
          specialtiesRaw: [],
          loadingActivitiesStatus: 'loading',
          specialtyFilter: config.entry.specialtyCode?.map(code => ({ id: code })) || ([] as any),
          searchTarget: ['onlyHCO', 'startWithHCO'].includes(config?.searchMode) ? SEARCH_TARGET.HCO : SEARCH_TARGET.HCP,
        });
        if (searchMapStore.searchTarget === SEARCH_TARGET.HCP) {
          searchLocationWithParams();
        } else {
          hcoSearchLocationWithParams();
        }
      });
    }

    configStore.setState(initConfig);
    if (!!initConfig.countryFilterSelected && !configStore.countriesAvailable.includes(initConfig.countryFilterSelected.toUpperCase() as any)) {
      configStore.setState({ countryFilterSelected: '' });
    }

    applyDefaultTheme(configStore.state.enableDarkMode);

    const parent = this.el.parentElement;
    parent.style.padding = '0';
    // add a position (if not defined) to parent element in order to stretch
    // the sdk wrapper dimensions using absolute position
    if (getComputedStyle(parent).position === 'static') {
      parent.style.position = 'relative';
    }

    this.parentEl = parent;

    const update = debounce(this.updateParentDims.bind(this), 100);

    update();

    const ro = new ResizeObserver(update);

    ro.observe(parent);
  }

  private getMapConfig(configInput) {
    if (configInput.useGoogleMap) {
      if (!configInput.googleMapApiKey) {
        throw new Error('Please provide Google Map API key');
      }

      return {
        provider: MapProvider.GOOGLE_MAP,
        googleMapApiKey: configInput.googleMapApiKey,
      };
    } else {
      return {
        provider: MapProvider.OPEN_STREETMAP,
        enableLeafletAttribution: configInput.enableLeafletAttribution,
      };
    }
  }

  observeChangeLang(targetElement: Element) {
    const observer = new MutationObserver(mutationsList => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'lang') {
          const lang = targetElement.getAttribute('lang');
          getI18nLabels(lang, this.i18nCustomObject);
        }
      }
    });
    if (!configStore.state.locale) {
      // Not allow to change lang by site's lang if Locale is hard configured
      observer.observe(targetElement, { attributes: true, childList: false, subtree: false });
    }
  }

  updateParentDims() {
    uiStore.setParentDims(this.parentEl.getBoundingClientRect());
  }

  retryFindGeoloc = err => {
    if (err.code === GEOLOC.TIMEOUT_CODE && this.retriesCounter < GEOLOC.MAX_TRIES) {
      this.retriesCounter = this.retriesCounter + 1;
      this.tryFindGeoloc();
    }
    if (err.code === GEOLOC.USER_DENIED) {
      const screenName = configStore.state.entry.screenName;
      const defaultLocationSearch = configStore.state.entry.defaultLocation;
      const isNearMeScreen = (screenName === 'searchByLocation' && defaultLocationSearch === 'nearMe') || screenName === 'searchNearMe';
      if (isNearMeScreen) {
        console.error(err.message);
        console.error('Unable to search `NearMe`');
      }
    }
  };

  tryFindGeoloc({ getCurrentPosition = undefined, disableCollectGeo = false } = {}) {
    const handler = (coords: GeolocCoordinates) => {
      getAddressFromGeo(coords.latitude, coords.longitude).then(res => {
        if (res?.address?.country_code) {
          configStore.setState({
            countryGeo: res.address.country_code,
          });

          const specialtyCode = configStore.state.entry?.specialtyCode;
          const specialtyLabel = configStore.state.entry?.specialtyLabel;
          const searchMode = configStore.initialSearchMode;
          if (specialtyCode && configStore.state.entry.screenName === SCREEN_NAME.SEARCH_NEAR_ME) {
            // screen Search near me must have specialtyCode and specialtyLabel
            this.searchNearMe({ specialtyCode, specialtyLabel });
          }
          if (configStore.state.entry.screenName === SCREEN_NAME.SEARCH_BY_LOCATION && configStore.state.entry.defaultLocation === 'nearMe') {
            this.searchNearMe({ specialtyCode, specialtyLabel, searchMode });
          }
        }
      });

      searchMapStore.setGeoLocation(coords);
    };

    if (getCurrentPosition) {
      getCurrentPosition(handler, err => {
        console.error(err || '[Geolocation] getCurrentPosition was error');
      });
    } else if (!disableCollectGeo) {
      defaultGetCurrentPosition(handler, this.retryFindGeoloc);
    }
  }

  findCurrentPosition({ getCurrentPosition, disableCollectGeo }: any) {
    if (!getCurrentPosition && !navigator.geolocation) {
      console.error('[Geolocation] is not supported by your browse');
    }

    if (getCurrentPosition || navigator.geolocation) {
      this.tryFindGeoloc({ getCurrentPosition, disableCollectGeo });
    }
  }

  loadCurrentPosition({ isShowcase, getCurrentPosition, disableCollectGeo }) {
    if (isShowcase) {
      // Canada - Toronto Geolocation
      searchMapStore.setGeoLocation({
        latitude: 43.6534817,
        longitude: -79.3839347,
      });
      return;
    }

    const dataGeolocation = storageUtils.getObject(OKSDK_GEOLOCATION_HISTORY);
    if (dataGeolocation && !disableCollectGeo) {
      const time = Number(dataGeolocation.time);
      if (dateUtils(time).diffMinuteFromNow() < GEOLOC.MINUTE_HISTORY) {
        const { latitude, longitude } = dataGeolocation;
        getAddressFromGeo(latitude, longitude).then(res => {
          if (res?.address?.country_code) {
            configStore.setState({
              countryGeo: res.address.country_code,
            });
          }
        });
        searchMapStore.setGeoLocation({ latitude, longitude });
      } else {
        storageUtils.remove(OKSDK_GEOLOCATION_HISTORY);
      }
    }

    if (disableCollectGeo) {
      storageUtils.remove(OKSDK_GEOLOCATION_HISTORY);
    }

    this.findCurrentPosition({ getCurrentPosition, disableCollectGeo });
  }

  loadInitScreenSearch() {
    routerStore.push(ROUTER_PATH.SEARCH_RESULT);
    searchMapStore.setState({
      navigatedFromHome: true,
    });
  }

  handleClickSDK(ev: any) {
    this.onClickSDK.emit(ev);
  }

  async loadCountriesFromMyKey({ apiKey }: { apiKey: string }) {
    return graphql
      .mySubscriptionKey({
        headers: {
          'Ocp-Apim-Subscription-Key': apiKey,
        },
      })
      .then(res => {
        if (!res.mySubscriptionKey?.countries) {
          return;
        }
        const countriesExtends = [];
        const listCodeBase = Object.keys(COUNTRY_CODE_BASE);
        res.mySubscriptionKey.countries.forEach(code => {
          if (listCodeBase.includes(code)) {
            countriesExtends.push(...COUNTRY_CODE_BASE[code]);
            return;
          }
          if (code === 'FULL') {
            return;
          }
          countriesExtends.push(code);
        });
        if (res.mySubscriptionKey.countries?.includes('FULL')) {
          countriesExtends.push(...GET_FULL_LIST_COUNTRY_CODE());
        }
        const countries = (countriesExtends as CountryCode[])
          .filter((code, idx, array) => COUNTRIES_LABELS[code] && array.indexOf(code) === idx)
          .sort((codeA: CountryCode, codeB: CountryCode) => {
            return COUNTRIES_LABELS[codeA].localeCompare(COUNTRIES_LABELS[codeB]);
          });
        // Remove code does not exist and duplicate element
        //   and sort by country label
        configStore.setState({
          countriesSubscriptionKey: countries, // ["FR", "US"]
        });
      })
      .catch(() => {}); // To avoid crash the app
  }

  renderWidgets() {
    if (this.widget === 'map') {
      return <hcl-sdk-widget-map widgetProps={this.widgetProps} />;
    }

    return null;
  }

  render() {
    const { screenSize, orientation, screenWidth } = uiStore.state.breakpoint;
    if (screenSize === 'unknown' || this.loading) {
      return null;
    }

    const classesDark = cls({
      'sdk-dark-mode': configStore.state.enableDarkMode,
      'sdk-map-dark-mode': configStore.state.enableMapDarkMode,
    });
    const classes = cls(`wrapper size-${screenSize} orientation-${orientation}`, classesDark, {
      'show-medical-term': configStore.state.enableMedicalTerm,
      'size-tablet-xs': screenWidth > BREAKPOINT_MAX_WIDTH.MOBILE_PORTRAIT && screenWidth < BREAKPOINT_MAX_WIDTH.TABLET_PORTRAIT,
      'size-desktop-sm': screenWidth >= BREAKPOINT_MAX_WIDTH.TABLET_PORTRAIT && screenWidth < BREAKPOINT_MAX_WIDTH.DESKTOP_SMALL,
    });

    return (
      <Host>
        <div onClick={ev => this.handleClickSDK(ev)}>
          {configStore.state.stylesheet ? <link rel="stylesheet" href={configStore.state.stylesheet} /> : null}

          {this.widget ? (
            <div class={classesDark}>{this.renderWidgets()}</div>
          ) : (
            <div class={classes}>
              <hcl-sdk-event-wrapper>
                <hcl-sdk-route component="hcl-sdk-home" path={ROUTER_PATH.MAIN}></hcl-sdk-route>
                <hcl-sdk-route
                  component="hcl-sdk-search-result"
                  path={ROUTER_PATH.SEARCH_RESULT}
                  slotMatrix={[{ receiveName: 'hcp-right-panel-route', grantName: 'hcp-right-panel-detail', show: !!this.slotState.rightHCP?.show }]}
                >
                  {this.slotState.rightHCP?.show && (
                    <div slot="hcp-right-panel-route">
                      <slot name="hcp-right-panel" />
                    </div>
                  )}
                </hcl-sdk-route>
                <hcl-sdk-route component="hcl-sdk-search" path={ROUTER_PATH.SEARCH} />

                <hcl-sdk-modal-center modals={configStore.state.modals} />
              </hcl-sdk-event-wrapper>
            </div>
          )}
        </div>
      </Host>
    );
  }
}
